import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { loadFont, optimizeFontSizeBinarySearch } from "../../../../../Utils/Ultility";
import { applySvgLabelPreview } from "../../../../../Utils/svgLabelPreview";
import { cssWithout, PADDING_PROPS } from "../../../../../Utils/svgLabelBadge";

const Preview = ({ data, type, design, typePage, device }) => {
  const ref = useRef(null);
  // Shops on the SVG renderer draw the shape, image and text inside the <svg> itself. Falling back
  // to the legacy DOM here would show the support agent a different label than the merchant's
  // storefront — the whole point of this preview.
  const useSvg = useSelector((state) => state.shop.svgLabelBadge);
  const [text, setText] = useState(data.text ?? "");

  const dynamicText = [
    "{SAVE_PERCENT}",
    "{SAVE_AMOUNT}",
    "{PRICE}",
    "{NEW_FOR}",
    "{STOCK}",
    "{MIN_PRICE}",
    "{MAX_PRICE}",
    "{MIN_SALE}",
    "{MAX_SALE}",
    "{NUMBER_OF_REVIEW}",
    "{AVERAGE_RATING}",
  ];

  useEffect(() => {
    if (data.text) {
      let tmp = data.text;
      dynamicText.forEach((token) => {
        if (tmp.includes(token)) tmp = tmp.replace(token, "XX");
      });
      setText(tmp);
    } else {
      setText(data.text ?? "");
    }
  }, [data]);

  useEffect(() => {
    const previewEl = ref.current;
    if (!previewEl) return;

    // Set by the trapezoid/triangle branch below, which keeps a ResizeObserver alive past the
    // effect body.
    let cleanup;

    previewEl.removeAttribute("style");

    // margin
    if (data.margin) {
      previewEl.style.margin = `${data.margin.top}px ${data.margin.right}px ${data.margin.bottom}px ${data.margin.left}px`;
    }

    // opacity
    if (data.opacity >= 0) {
      previewEl.style.opacity = `${data.opacity}`;
    }

    // border + radius — on the SVG branch the shape carries its own rounding and stroke, so a CSS
    // border here would double up with the SVG stroke.
    if (useSvg) {
      previewEl.style.display = "block";
      previewEl.style.overflow = "visible";
    } else {
      if (design?.border_radius != null) {
        previewEl.style.borderRadius = `${design.border_radius}px`;
      }
      if (design?.border) {
        previewEl.style.border = `${design.border.size}px ${design.border.style} ${design.border.color}`;
      }
    }

    const dataType = data[`${type}_type`];
    const textType = data[`${type}_text_type`];

    // background color — SVG branch paints the shape inside the <svg> instead.
    //
    // No padding on the <svg> either, for the same reason `data.styles` goes through
    // cssWithout(PADDING_PROPS) below: padding shrinks the SVG viewport instead of insetting the
    // drawing. aspect-ratio sizes the border box, so a badge 10px tall minus 10px of vertical
    // padding leaves a 0-height viewport and the preview renders nothing at all. buildTextScene
    // already insets the text inside the viewBox.
    if (!useSvg && dataType === 1) {
      if (textType === 0 && design?.shape_color?.length) {
        previewEl.style.backgroundColor = design.shape_color;
      }
      if (
        textType === 1 &&
        design?.shape_start_color?.length &&
        design?.shape_ending_color?.length
      ) {
        previewEl.style.background = `linear-gradient(${design.shape_gradient_direction}deg, ${design.shape_start_color}, ${design.shape_ending_color})`;
      }
      if (textType === 2) {
        previewEl.style.backgroundColor = null;
      }
      if (type === "badge") previewEl.style.padding = "5px";
    } else {
      previewEl.style.backgroundColor = null;
    }

    // position (label only)
    if (type === "label") {
      if (data.customize_position) {
        previewEl.style.top = `${data.top}%`;
        previewEl.style.left = `${data.left}%`;
      } else {
        switch (data.position) {
          case "top_left":
            previewEl.style.top = "0px";
            previewEl.style.left = "0px";
            break;
          case "top_center":
            previewEl.style.top = "0px";
            previewEl.style.left = "50%";
            previewEl.style.translate += "-50% 0";
            break;
          case "top_right":
            previewEl.style.top = "0px";
            previewEl.style.right = "0px";
            break;
          case "middle_left":
            previewEl.style.top = "50%";
            previewEl.style.left = "0px";
            previewEl.style.translate += "0 -50%";
            break;
          case "middle_center":
            previewEl.style.top = "50%";
            previewEl.style.left = "50%";
            previewEl.style.translate += "-50% -50%";
            break;
          case "middle_right":
            previewEl.style.top = "50%";
            previewEl.style.right = "0px";
            previewEl.style.translate += "0 -50%";
            break;
          case "bottom_left":
            previewEl.style.bottom = "0px";
            previewEl.style.left = "0px";
            break;
          case "bottom_center":
            previewEl.style.bottom = "0px";
            previewEl.style.left = "50%";
            previewEl.style.translate += "-50% 0";
            break;
          case "bottom_right":
            previewEl.style.bottom = "0px";
            previewEl.style.right = "0px";
            break;
          default:
            break;
        }
      }
    }

    // badge position
    if (type === "badge") {
      switch (data.predefined_position) {
        case "left":
          previewEl.style.left = "0";
          break;
        case "center":
          previewEl.style.left = `calc(50% - ${data.margin?.right ?? 0}px)`;
          previewEl.style.translate += "-50% 0";
          break;
        case "right":
          previewEl.style.left = `calc(100% - ${data.margin?.right ?? 0}px)`;
          previewEl.style.translate += "-100% 0";
          break;
        default:
          break;
      }
    }

    // size
    let width, height, fontSize;
    const baseW = type === "label" ? data.label_width : data.badge_width;
    const baseH = type === "label" ? data.label_height : data.badge_height;
    if (typePage === "collection") {
      if (device === "desktop") {
        width = baseW;
        height = baseH;
        fontSize = design?.font_size;
      } else {
        width = (type === "label" ? data.label_width_mobile : data.badge_width_mobile) || baseW;
        height = (type === "label" ? data.label_height_mobile : data.badge_height_mobile) || baseH;
        fontSize = design?.font_size_mobile || design?.font_size;
      }
    } else {
      if (device === "desktop") {
        width = (type === "label" ? data.label_width_product : data.badge_width_product) || baseW;
        height =
          (type === "label" ? data.label_height_product : data.badge_height_product) || baseH;
        fontSize = design?.font_size_product || design?.font_size;
      } else {
        width =
          (type === "label" ? data.label_width_product_mobile : data.badge_width_product_mobile) ||
          baseW;
        height =
          (type === "label"
            ? data.label_height_product_mobile
            : data.badge_height_product_mobile) || baseH;
        fontSize = design?.font_size_product_mobile || design?.font_size;
      }
    }

    const unit = data.fixed_size ? "px" : "%";
    previewEl.style.width = `${width}${unit}`;

    if (useSvg) {
      // The <svg> gets its height from the viewBox aspect (written by applyAspect). A percentage
      // height would tie the box aspect to the parent instead, which no longer matches the viewBox
      // and would stretch the text. Same reason aspect-ratio is left alone for badges below.
      previewEl.style.height = "auto";
    } else if (type === "label") {
      previewEl.style.height = data.image && data.ratio_size ? "auto" : `${height}${unit}`;
    } else {
      previewEl.style.height = "auto";
      if (!data.ratio_size || dataType === 1) {
        previewEl.style.aspectRatio = `${width}/${height}`;
      }
    }

    // animation & custom styles
    if (data.animation) previewEl.style.cssText += `animation-name: ${data.animation};`;
    // Padding stays off the <svg> (it would shrink the shape); buildTextScene insets the text.
    if (data.styles) {
      previewEl.style.cssText += useSvg ? cssWithout(data.styles, PADDING_PROPS) : data.styles;
    }

    // SVG branch: draw shape + image + text now that the box is sized, and stop — everything below
    // styles the legacy DOM children, which do not exist here.
    if (useSvg) {
      applySvgLabelPreview(previewEl, {
        data,
        design,
        type,
        page: typePage === "collection" ? "collection" : "product",
        isMobile: device !== "desktop",
        text: dataType === 1 ? text ?? "" : "",
        width,
        height,
      });
      if (data.rotate > 0) previewEl.style.rotate = `${data.rotate}deg`;
      return;
    }

    // text element
    const textElement = previewEl.querySelector(".pl-text");
    if (dataType === 1 && textElement) {
      textElement.removeAttribute("style");
      if (data.image && type === "badge") textElement.style.position = "absolute";
      textElement.style.color = design?.text_color;
      if (design?.font_family && design.font_family !== "default") {
        loadFont(design.font_family);
        textElement.style.fontFamily = `'${design.font_family}'`;
      }
      textElement.style.fontSize = `${fontSize}px`;
      textElement.style.letterSpacing = `${design?.letter_spacing ?? 0}px`;
      if (design?.font_style) {
        textElement.style.fontWeight = design.font_style.weight;
        textElement.style.fontStyle = design.font_style.style;
        textElement.style.textDecoration = design.font_style.decoration;
      }
      if (data.text_styles) textElement.style.cssText += data.text_styles;
      textElement.style.wordBreak = "break-word";
      textElement.innerHTML = text;

      if (data.auto_responsive) {
        const initialFontSize = parseFloat(window.getComputedStyle(textElement).fontSize);
        let targetHeight = previewEl.clientHeight * (data.responsive_ratio || 1);
        if (targetHeight > 0 && initialFontSize > 0) {
          optimizeFontSizeBinarySearch(textElement, targetHeight, initialFontSize);
        }
        if (data.image) {
          const img = previewEl.querySelector("img");
          if (img) {
            img.onload = () => {
              targetHeight = img.clientHeight * (data.responsive_ratio || 1);
              optimizeFontSizeBinarySearch(textElement, targetHeight, initialFontSize);
            };
          }
        }
      }

      if (
        !data.image &&
        (design?.shape_class === "pl-text-trapezoid" || design?.shape_class === "pl-text-triangle")
      ) {
        const applyTextRotation = () => {
          const rect = previewEl.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const deg = Math.atan(rect.height / rect.width) * (180 / Math.PI);
          textElement.style.transform = `translateX(-50%) translateY(-50%) rotate(${deg}deg)`;
        };
        applyTextRotation();
        const observer = new ResizeObserver(applyTextRotation);
        observer.observe(previewEl);
        cleanup = () => observer.disconnect();
      }
    }

    // rotation
    if (data.rotate > 0) {
      previewEl.style.rotate = `${data.rotate}deg`;
    }

    return cleanup;
  }, [data, design, text, typePage, device, useSvg]);

  const rootClass = `${
    type === "label" ? "pl-label-preview pl-image" : "pl-badge-preview pl-badge-image"
  } pl-animation`;

  // SVG branch: contents (shape, image, text, gradient) are drawn by layoutSvgNode. The shape class
  // is deliberately absent — its clip-path would cut the SVG a second time.
  if (useSvg) return <svg ref={ref} className={rootClass} />;

  return (
    <div ref={ref} className={`${rootClass} ${data.image ? "" : design?.shape_class ?? ""}`}>
      {data.image && <img className="pl-w-100" src={data.image} alt={data.name} loading="lazy" />}
      {data[`${type}_type`] === 1 && <span className="pl-text"></span>}
    </div>
  );
};

Preview.defaultProps = {
  typePage: "collection",
  device: "desktop",
};

Preview.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string,
    image: PropTypes.string,
    name: PropTypes.string,
    fixed_size: PropTypes.bool,
    rotate: PropTypes.number,
    opacity: PropTypes.number,
    responsive_ratio: PropTypes.number,
    auto_responsive: PropTypes.bool,
    ratio_size: PropTypes.bool,
    label_width: PropTypes.number,
    label_height: PropTypes.number,
    label_width_mobile: PropTypes.number,
    label_height_mobile: PropTypes.number,
    label_width_product: PropTypes.number,
    label_height_product: PropTypes.number,
    label_width_product_mobile: PropTypes.number,
    label_height_product_mobile: PropTypes.number,
    badge_width: PropTypes.number,
    badge_height: PropTypes.number,
    badge_width_mobile: PropTypes.number,
    badge_height_mobile: PropTypes.number,
    badge_width_product: PropTypes.number,
    badge_height_product: PropTypes.number,
    badge_width_product_mobile: PropTypes.number,
    badge_height_product_mobile: PropTypes.number,
    animation: PropTypes.string,
    styles: PropTypes.string,
    text_styles: PropTypes.string,
    link: PropTypes.string,
    tooltip: PropTypes.string,
    position: PropTypes.string,
    customize_position: PropTypes.bool,
    top: PropTypes.number,
    left: PropTypes.number,
    predefined_position: PropTypes.string,
    margin: PropTypes.shape({
      top: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number,
    }),
  }).isRequired,
  type: PropTypes.string.isRequired,
  design: PropTypes.shape({
    shape_class: PropTypes.string,
    shape_color: PropTypes.string,
    shape_start_color: PropTypes.string,
    shape_ending_color: PropTypes.string,
    shape_gradient_direction: PropTypes.number,
    border_radius: PropTypes.number,
    text_color: PropTypes.string,
    font_family: PropTypes.string,
    font_size: PropTypes.number,
    font_size_mobile: PropTypes.number,
    font_size_product: PropTypes.number,
    font_size_product_mobile: PropTypes.number,
    letter_spacing: PropTypes.number,
    border: PropTypes.shape({
      size: PropTypes.number,
      style: PropTypes.string,
      color: PropTypes.string,
    }),
    font_style: PropTypes.shape({
      style: PropTypes.string,
      decoration: PropTypes.string,
      weight: PropTypes.string,
    }),
  }).isRequired,
  typePage: PropTypes.oneOf(["collection", "product"]),
  device: PropTypes.oneOf(["desktop", "mobile"]),
};

export default Preview;
